const { METERS_SQUARED, METERS } = require('../constants.js');
const { useRooms } = require('../composables/useRooms.js');

const RoomForm = {
	props: {
		variant: {
			type: String,
			default: 'product',
			validator: (v) => ['product', 'category'].includes(v)
		},
		prefillData: {
			type: Object,
			default: null
		}
	},

	emits: ['room-saved', 'room-deleted', 'rooms-changed'],

	setup(props, { emit }) {
		const { watch, onMounted } = Vue;

		const roomState = useRooms({
			onRoomsChanged: () => {
				emit('rooms-changed', {
					rooms: roomState.rooms.value,
					primaryRoomIndex: roomState.primaryRoomIndex.value
				});
			},
			onRoomDeleted: ({ deletedIndex }) => {
				emit('room-deleted', {
					rooms: roomState.rooms.value,
					primaryRoomIndex: roomState.primaryRoomIndex.value,
					deletedIndex
				});
			}
		});

		// Wrap saveRoomData to also emit room-saved
		const originalSave = roomState.saveRoomData;
		roomState.saveRoomData = () => {
			const saved = originalSave();
			if (saved) {
				emit('room-saved', {
					rooms: roomState.rooms.value,
					primaryRoomIndex: roomState.primaryRoomIndex.value
				});
			}
		};

		// Watch rooms + primaryRoomIndex for changes (e.g. from setPrimaryRoom)
		watch(
			[roomState.rooms, roomState.primaryRoomIndex],
			() => {
				emit('rooms-changed', {
					rooms: roomState.rooms.value,
					primaryRoomIndex: roomState.primaryRoomIndex.value
				});
			}
		);

		onMounted(() => {
			roomState.loadRooms();

			// Prefill form from parent data (product page "Save this as a room" flow)
			if (props.prefillData) {
				roomState.createNewRoom.value = true;
				roomState.unitType.value = props.prefillData.unitType || 'm2';

				if (props.prefillData.width && props.prefillData.length) {
					roomState.inputMode.value = 'dimensions';
					roomState.width.value = Number(props.prefillData.width);
					roomState.length.value = Number(props.prefillData.length);
				} else if (props.prefillData.area) {
					roomState.inputMode.value = 'total-area';
					roomState.directArea.value = Number(props.prefillData.area);
				}
			}
		});

		return {
			...roomState,
			METERS_SQUARED,
			METERS,
			variant: props.variant
		};
	},

	template: `
		<div class="w-full">
			<!-- Category: "Calculate your flooring" initial button -->
			<button
				v-if="variant === 'category' && rooms.length === 0 && !createNewRoom"
				class="w-full btn btn-secondary btn-icon lg:w-auto"
				@click="createNewRoom = true"
			>
				<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 384 512">
					<path d="M336 176H48V464H336V176zm0-48V48H48v80H336zm48 0v24 24V464v48H336 48 0V464 176 152 128 48 0H48 336h48V48v80zM80 208h48v48H80V208zm48 88v48H80V296h48zM80 384H216v48H80V384zM216 208v48H168V208h48zm-48 88h48v48H168V296zm136-88v48H256V208h48zm-48 88h48v48H256V296zm48 88v48H256V384h48z" />
				</svg>
				<span>Calculate your flooring</span>
			</button>

			<!-- Room creation / edit form -->
			<div
				v-else-if="createNewRoom || rooms.length == 0 || modifyRoom"
				:class="variant === 'category' ? 'p-4 w-full rounded-md bg-shades-50' : 'w-full bg-gray-100 rounded-md'"
			>
				<div class="flex justify-between items-center">
					<component
						:is="variant === 'product' ? 'h3' : 'div'"
						v-if="createNewRoom || rooms.length == 0"
						class="m-0 text-xl font-bold text-shades-800"
					>
						Create your room
					</component>
					<component
						:is="variant === 'product' ? 'h3' : 'div'"
						v-else
						class="m-0 text-xl font-bold text-shades-800"
					>
						Update your room
					</component>
				</div>

				<component
					:is="variant === 'product' ? 'h4' : 'div'"
					v-if="createNewRoom || rooms.length == 0"
					:class="variant === 'category'
						? 'p-0 m-0 mt-2 text-sm font-normal text-shades-700'
						: 'mt-4 text-sm font-normal text-shades-700'"
				>
					{{ variant === 'category'
						? 'Please enter your measurements below to create your custom room size.'
						: 'Save your room measurements to see prices across search results and other products.' }}
				</component>
				<component
					:is="variant === 'product' ? 'h4' : 'div'"
					v-else
					:class="variant === 'category'
						? 'mt-3 text-sm font-normal text-shades-700'
						: 'mt-4 text-sm font-normal text-shades-700'"
				>
					Update your existing room.
				</component>

				<div :class="variant === 'category' ? 'my-5' : 'mt-[30px] mb-5'">
					<div class="input-label">Room name</div>
					<input
						type="text"
						v-model="roomName"
						class="w-full input-field"
						placeholder="Living Room, Dining Room, etc.."
						maxlength="20"
					/>
				</div>

				<div
					:class="variant === 'category'
						? 'flex flex-wrap gap-5 justify-start items-center md:flex-nowrap'
						: 'flex flex-row gap-5 justify-start items-center mb-5'"
				>
					<div class="w-full calc_meaurement-type-block">
						<div class="input-label">Measurement type</div>
						<div :class="variant === 'category' ? 'toggle-wrapper' : 'toggle-wrapper type-toggle-main'">
							<div class="toggle-container" @click="toggleInputMode">
								<div id="areaCheck" :class="{ checked: inputMode === 'total-area' }">Area</div>
								<div id="dimensionCheck" :class="{ checked: inputMode === 'dimensions' }">W x L</div>
								<div class="slider"></div>
							</div>
						</div>
					</div>

					<div v-if="inputMode === 'dimensions'" class="w-auto calc_dimension-wrappers">
						<div :class="variant === 'category' ? 'flex w-full' : 'flex space-x-2'">
							<div :class="variant === 'category' ? 'w-full basis-1/2 input-wrapper' : 'w-full input-wrapper'">
								<div class="input-label">Width</div>
								<div class="relative">
									<input
										type="number"
										min="0"
										max="1000"
										v-model.number="width"
										placeholder="E.g. 2"
										:class="variant === 'category'
											? 'input-field calc_measurement-input-block noscroll'
											: 'w-full input-field noscroll'"
									/>
									<span class="input-suffix">{{ METERS }}</span>
								</div>
							</div>
							<div :class="variant === 'category' ? 'w-full basis-1/2 input-wrapper' : 'w-full input-wrapper'">
								<div class="input-label">Length</div>
								<div class="relative">
									<input
										type="number"
										min="0"
										max="1000"
										v-model.number="length"
										placeholder="E.g. 2"
										class="w-full input-field noscroll"
									/>
									<span class="input-suffix">{{ METERS }}</span>
								</div>
							</div>
						</div>
					</div>

					<div v-else :class="variant === 'category' ? 'w-full max-w-[180px]' : 'w-full'">
						<div :class="variant === 'category' ? 'flex w-full max-w-[180px]' : ''">
							<div class="w-full input-wrapper">
								<div class="input-label">Area</div>
								<div class="relative">
									<input
										type="number"
										min="0"
										max="1000"
										v-model.number="directArea"
										placeholder="E.g. 2"
										class="w-full input-field noscroll"
									/>
									<span class="input-suffix">{{ METERS_SQUARED }}</span>
								</div>
							</div>
						</div>
					</div>

					<!-- Category: save button inline -->
					<div v-if="variant === 'category'" class="room-button-wrapper">
						<div class="hidden input-label md:block">&nbsp;</div>
						<button @click="saveRoomData" class="btn btn-darkGrey btn-icon">
							<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
								<path d="M248 72V48H200V72 232H40 16v48H40 200V440v24h48V440 280H408h24V232H408 248V72z" />
							</svg>
							<span>{{ (createNewRoom || rooms.length == 0) ? "Save room" : "Update room" }}</span>
						</button>
					</div>
				</div>

				<!-- Product: save button below -->
				<div v-if="variant === 'product'" class="">
					<button @click="saveRoomData" class="btn btn-darkGrey btn-icon">
						<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
							<path d="M248 72V48H200V72 232H40 16v48H40 200V440v24h48V440 280H408h24V232H408 248V72z" />
						</svg>
						<span>{{ (createNewRoom || rooms.length == 0) ? "Save room" : "Update room" }}</span>
					</button>
				</div>
			</div>

			<!-- Room list view -->
			<div v-else :class="variant === 'category' ? 'p-4 w-full rounded-md bg-shades-50' : 'w-full'">
				<div class="text-xl font-bold text-shades-800 leading-none mb-[30px]">
					Your rooms
				</div>
				<div class="mb-4">
					<div for="room-select" class="input-label">Selected Room</div>
					<div class="input-wrapper">
						<div :class="variant === 'category'
							? 'flex flex-wrap items-center space-x-5 md:flex-nowrap'
							: 'flex flex-wrap items-center space-x-5 sm:flex-nowrap'"
						>
							<div class="select-field-wrapper room-select-field">
								<select
									v-model="primaryRoomIndex"
									@change="setPrimaryRoom($event.target.value)"
									id="room-select"
									class="w-full"
								>
									<option
										v-for="(room, index) in rooms"
										:key="index"
										:value="index + 1"
									>
										{{ room.roomName }} ({{ room.projectSize }} {{ METERS_SQUARED }})
									</option>
								</select>
							</div>
							<div
								class="font-bold transition-all ease-linear cursor-pointer text-shades-400 hover:text-shades-500"
								@click="editRoom"
							>
								Edit
							</div>
							<div
								class="delete-room-button"
								:class="{ confirm: confirmDelete }"
								@click="deleteRoom"
							>
								{{ confirmDelete ? "Confirm" : "Delete" }}
							</div>
							<div
								class="delete-room-button cancel"
								v-if="confirmDelete"
								@click="confirmDelete = false"
							>
								Cancel
							</div>
							<!-- Product: inline "+ New" link -->
							<div
								v-if="variant === 'product'"
								class="font-bold transition-all ease-linear cursor-pointer text-brand-500 hover:text-brand-600"
								@click="createNewRoom = true"
							>
								+ New
							</div>
						</div>
					</div>
				</div>

				<!-- Category: full "Create new room" button -->
				<button
					v-if="variant === 'category'"
					@click="createNewRoom = true"
					class="btn btn-darkGrey btn-icon"
				>
					<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 448 512">
						<path d="M248 72V48H200V72 232H40 16v48H40 200V440v24h48V440 280H408h24V232H408 248V72z" />
					</svg>
					<span>Create new room</span>
				</button>

				<!-- Login suggestion for guests with rooms -->
				<div v-if="rooms.length > 0 && !isLoggedIn" class="mt-3 text-sm text-shades-500">
					<span class="inline-flex gap-1.5 items-center">
						<svg class="flex-shrink-0 w-3.5 h-3.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
							<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
								d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
						</svg>
						<span>
							<button @click="openAuthModal('login')" class="p-0 m-0 text-sm underline bg-transparent border-none cursor-pointer hover:text-shades-700 text-shades-500">Log in</button>
							or
							<button @click="openAuthModal('register')" class="p-0 m-0 text-sm underline bg-transparent border-none cursor-pointer hover:text-shades-700 text-shades-500">create an account</button>
							to keep your rooms saved
						</span>
					</span>
				</div>

				<!-- Slot for additional content (e.g. AreaCalculator on product page) -->
				<slot name="room-list-extra"></slot>
			</div>
		</div>
	`
};

module.exports = { RoomForm };
